import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTicketStore } from '../store/ticketStore';
import { useRbacStore } from '../store/rbacStore';
import { useBrandingStore } from '../store/brandingStore';
import { cn } from '../lib/utils';
import { Logo } from './Logo';
import { CheckInOutButton } from './attendance/CheckInOut';
import { Avatar, AvatarFallback, AvatarImage } from './Avatar';
import { getUserAvatarUrl } from '../utils/avatar';
import { getEffectiveRoles } from '../utils/supportRoles';

import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './DropdownMenu';
import { Users, Briefcase, DollarSign, Box, TrendingUp, Settings, Calendar, UsersRound, CalendarOff, Receipt, UserPlus, Target, Clock, Shield, Activity, FolderKanban, ListTodo, Presentation, BoxSelect, Code, Calculator, AlertTriangle, Building, ScrollText, CreditCard, HandCoins, Building2, ShoppingCart, ShoppingBag, FileText, Landmark, Key, Server, Wrench, Ticket, BookOpen, PhoneCall, Handshake, ShieldAlert, BadgeInfo, NotebookText, Video, Megaphone, Lightbulb, Link as LinkIcon, CheckSquare, Network, Share2, ShieldCheck, FileSearch, Globe, MessageSquare, FileClock, UserCog, BarChart3, Headset, ChevronsUpDown, User as UserIcon, PanelLeftClose, PanelLeftOpen, UserCheck, LogOut, LayoutDashboard, ClipboardList, Warehouse, Truck, Tags, CalendarDays, GraduationCap, Bot, LineChart, KeyRound, RefreshCw, PartyPopper } from 'lucide-react';

/**
 * `permission` names the module/resource a nav entry maps to. Entries carrying
 * one are hidden unless the signed-in role holds View on it, so an Employee
 * never sees a screen the API would reject. Entries without one are always
 * shown (no permission defined for them yet).
 */
type NavChild = {
  iconKey: string;
  label: string;
  path: string;
  badge?: string | number;
  section?: string;
  permission?: { module: string; resource: string; action?: string; company?: boolean };
};
type NavItem = {
  iconKey: string;
  label: string;
  path: string;
  children?: NavChild[];
};

const IconMap: Record<string, React.ElementType> = {
  dashboard: LayoutDashboard,
  requests: ClipboardList,
  approval: CheckSquare,
  stock: Warehouse,
  service: Wrench,
  warranty: ShieldCheck,
  po: ScrollText,
  vendors: Truck,
  categories: Tags,
  people: Users, work: Briefcase, money: DollarSign, assets: Box,
  support: Headset, growth: TrendingUp, admin: UserCog,
  attendance: Calendar, employees: UsersRound, leave: CalendarOff,
  payroll: Receipt, recruitment: UserPlus, performance: Target,
  shifts: Clock, workforce: Shield, onboarding: Activity,
  projects: FolderKanban, tasks: ListTodo, timesheets: Clock,
  whiteboard: Presentation, resources: BoxSelect, devops: Code,
  finance: Calculator, incidents: AlertTriangle, accounting: Building,
  invoices: ScrollText, expenses: CreditCard, financials: HandCoins,
  customers: Building2, sales: ShoppingCart, procurement: ShoppingBag,
  billing: FileText, finops: Landmark, tax: ScrollText, reports: BarChart3,
  registry: ScrollText, licenses: Key, digital: Server, maintenance: Wrench,
  tickets: Ticket, knowledge: BookOpen, callcenter: PhoneCall,
  portal: Globe, chat: MessageSquare, history: FileClock,
  clients: Handshake, sla: ShieldAlert, visitors: BadgeInfo,
  wiki: NotebookText, meetings: Video, marketing: Megaphone, ideas: Lightbulb,
  documents: LinkIcon, organization: Network, users: Users, roles: Share2,
  security: ShieldCheck, audit: FileSearch, subscription: CreditCard,
  api: Code, system: Settings, tasksquare: CheckSquare,
  candidates: UserCheck, interviews: CalendarDays, offboarding: LogOut, profile: UserIcon,
  training: GraduationCap, orgchart: Network, holidays: Calendar,
  policies: BookOpen, ai: Bot, settings: Settings, analytics: LineChart,
  calendar: Calendar, gantt: LineChart, sprints: Target, workflows: Share2,
  automation: Activity, approvals: ShieldCheck,
  vault: KeyRound, vaults: KeyRound, rotation: RefreshCw,
  engagement: PartyPopper,
};

const navItems: NavItem[] = [
  {
    iconKey: 'people', label: 'People', path: '/people',
    children: [
      { iconKey: 'dashboard', label: 'Dashboard', path: '/people' },
      { iconKey: 'profile', label: 'My Profile', path: '/people/me' },
      { iconKey: 'employees', label: 'Employees', path: '/people/employees', permission: { module: 'People', resource: 'Employees' } },
      { iconKey: 'attendance', label: 'Attendance', path: '/people/attendance', permission: { module: 'Attendance', resource: 'Dashboard' } },
      { iconKey: 'leave', label: 'Leave', path: '/people/leave', permission: { module: 'Leave', resource: 'Leave Requests' } },
      { iconKey: 'policies', label: 'Leave Policies', path: '/people/leave-policies', permission: { module: 'Leave', resource: 'Leave Types', action: 'Edit' } },
      { iconKey: 'payroll', label: 'Payroll', path: '/people/payroll', permission: { module: 'Payroll', resource: 'Payroll Records' } },
      { iconKey: 'shifts', label: 'Shifts', path: '/people/shifts', permission: { module: 'Shifts', resource: 'Shift Assignments' } },
      { iconKey: 'holidays', label: 'Holidays', path: '/people/holidays', permission: { module: 'People', resource: 'Holidays' } },
      { iconKey: 'engagement', label: 'Engagement', path: '/people/engagement' },
      { iconKey: 'recruitment', label: 'Recruitment', path: '/people/recruitment', badge: 6, permission: { module: 'Recruitment', resource: 'Jobs' } },
      { iconKey: 'candidates', label: 'Candidates', path: '/people/candidates', permission: { module: 'Recruitment', resource: 'Candidates' } },
      { iconKey: 'interviews', label: 'Interviews', path: '/people/interviews', permission: { module: 'Recruitment', resource: 'Interviews' } },
      { iconKey: 'onboarding', label: 'Onboarding', path: '/people/onboarding', permission: { module: 'Onboarding', resource: 'Onboarding Requests' } },
      { iconKey: 'offboarding', label: 'Offboarding', path: '/people/offboarding', permission: { module: 'Recruitment', resource: 'Offboarding' } },
      { iconKey: 'performance', label: 'Performance', path: '/people/performance', permission: { module: 'Performance', resource: 'Reviews' } },
      { iconKey: 'training', label: 'Training', path: '/people/training', permission: { module: 'Training', resource: 'Training Modules' } },
      { iconKey: 'orgchart', label: 'Org Chart', path: '/people/orgchart', permission: { module: 'People', resource: 'Org Chart' } },
      { iconKey: 'workforce', label: 'Workforce', path: '/people/workforce', permission: { module: 'Workforce', resource: 'Service Orders' } },
      { iconKey: 'documents', label: 'Documents', path: '/people/documents', permission: { module: 'People', resource: 'Employee Documents' } },
      { iconKey: 'policies', label: 'Policies', path: '/people/policies', permission: { module: 'People', resource: 'HR Policies' } },
      { iconKey: 'reports', label: 'Reports', path: '/people/reports', permission: { module: 'People', resource: 'Reports' } },
      { iconKey: 'settings', label: 'Settings', path: '/people/settings', permission: { module: 'People', resource: 'Settings' } },
    ],
  },
  {
    iconKey: 'work', label: 'Work', path: '/work',
    children: [
      { iconKey: 'projects', label: 'Projects', path: '/work/projects', permission: { module: 'Work', resource: 'Projects' } },
      { iconKey: 'tasks', label: 'Tasks', path: '/work/tasks', permission: { module: 'Work', resource: 'Tasks' } },
      { iconKey: 'sprints', label: 'Sprints', path: '/work/sprints', permission: { module: 'Work', resource: 'Sprints' } },
      { iconKey: 'gantt', label: 'Gantt', path: '/work/gantt', permission: { module: 'Work', resource: 'Projects' } },
      { iconKey: 'timesheets', label: 'Timesheets', path: '/work/timesheets', permission: { module: 'Work', resource: 'Timesheets' } },
      { iconKey: 'whiteboard', label: 'Whiteboard', path: '/work/whiteboard', permission: { module: 'Work', resource: 'Whiteboards' } },
      { iconKey: 'calendar', label: 'Calendar', path: '/work/calendar', permission: { module: 'Work', resource: 'Meetings' } },
      { iconKey: 'meetings', label: 'Meetings', path: '/work/meetings', permission: { module: 'Work', resource: 'Meetings' } },
      { iconKey: 'documents', label: 'Documents', path: '/work/documents', permission: { module: 'Work', resource: 'Documents' } },
      { iconKey: 'notes', label: 'Notes', path: '/work/notes', permission: { module: 'Work', resource: 'Notes' } },
      { iconKey: 'resources', label: 'Resources', path: '/work/resources', permission: { module: 'Work', resource: 'Resources' } },
      { iconKey: 'finance', label: 'Finance', path: '/work/finance', permission: { module: 'Work', resource: 'Finance' } },
      { iconKey: 'devops', label: 'DevOps', path: '/work/devops', permission: { module: 'Work', resource: 'DevOps' } },
      { iconKey: 'incidents', label: 'Incidents', path: '/work/incidents', badge: 2, permission: { module: 'Work', resource: 'Incidents' } },
      { iconKey: 'approvals', label: 'Approvals', path: '/work/approvals', permission: { module: 'Work', resource: 'Approvals' } },
      { iconKey: 'reports', label: 'Reports', path: '/work/reports', permission: { module: 'Work', resource: 'Reports' } },
      { iconKey: 'ai', label: 'AI Assistant', path: '/work/ai' },
      { iconKey: 'workflows', label: 'Workflows', path: '/work/workflows' },
      { iconKey: 'automation', label: 'Automation', path: '/work/automation' },
      { iconKey: 'settings', label: 'Settings', path: '/work/settings', permission: { module: 'Work', resource: 'Settings' } },
    ],
  },
  {
    iconKey: 'money', label: 'Money', path: '/money',
    children: [
      { iconKey: 'invoices', label: 'Invoices', path: '/money/invoices' },
      { iconKey: 'expenses', label: 'Expenses', path: '/money/expenses' },
      { iconKey: 'financials', label: 'Financials', path: '/money/financials' },
      { iconKey: 'accounting', label: 'Accounting', path: '/money/accounting' },
      { iconKey: 'customers', label: 'Customers', path: '/money/customers' },
      { iconKey: 'sales', label: 'Sales CRM', path: '/money/sales' },
      { iconKey: 'procurement', label: 'Procurement', path: '/money/procurement' },
      { iconKey: 'billing', label: 'Billing', path: '/money/billing' },
      { iconKey: 'finops', label: 'FinOps', path: '/money/finops' },
      { iconKey: 'tax', label: 'Tax Center', path: '/money/tax' },
      { iconKey: 'reports', label: 'Reports', path: '/money/reports' },
    ],
  },
  {
    iconKey: 'assets', label: 'Asset', path: '/assets',
    children: [
      { iconKey: 'dashboard', label: 'Dashboard', path: '/assets' },
      { iconKey: 'registry', label: 'Asset Management', path: '/assets/registry', permission: { module: 'Assets', resource: 'Assets', company: true } },
      { iconKey: 'requests', label: 'Requests & Approvals', path: '/assets/requests', permission: { module: 'Assets', resource: 'Asset Requests' } },
      { iconKey: 'stock', label: 'Stock & Inventory', path: '/assets/stock', permission: { module: 'Assets', resource: 'Assets', action: 'Edit', company: true } },
      { iconKey: 'service', label: 'Service & Maintenance', path: '/assets/service', permission: { module: 'Assets', resource: 'Maintenance', company: true } },
      { iconKey: 'warranty', label: 'Warranty Tracker', path: '/assets/warranty', permission: { module: 'Assets', resource: 'Assets', company: true } },
      { iconKey: 'po', label: 'Purchase Orders', path: '/assets/po', permission: { module: 'Assets', resource: 'Procurement', company: true } },
      { iconKey: 'vendors', label: 'Vendors', path: '/assets/vendors', permission: { module: 'Assets', resource: 'Procurement', company: true } },
      { iconKey: 'history', label: 'Audit History', path: '/assets/history', permission: { module: 'Assets', resource: 'Assets', company: true } },
      { iconKey: 'system', label: 'Settings', path: '/assets/settings', permission: { module: 'Assets', resource: 'Asset Settings', company: true } },
    ],
  },
  {
    iconKey: 'support', label: 'Support', path: '/support',
    children: [
      { iconKey: 'dashboard', label: 'Dashboard', path: '/support' },
      { iconKey: 'tickets', label: 'Tickets', path: '/support/tickets', permission: { module: 'Support', resource: 'Tickets' } },
      { iconKey: 'chat', label: 'Live Chat', path: '/support/chat', permission: { module: 'Support', resource: 'Live Chat' } },
      { iconKey: 'knowledge', label: 'Knowledge Base', path: '/support/knowledge', badge: 'Beta', permission: { module: 'Support', resource: 'Knowledge Base' } },
      { iconKey: 'history', label: 'Audit Log', path: '/support/history', permission: { module: 'Support', resource: 'Support Audit Log', company: true } },
      { iconKey: 'reports', label: 'Reports', path: '/support/reports', permission: { module: 'Support', resource: 'Support Reports', company: true } },
      { iconKey: 'system', label: 'Settings', path: '/support/settings', permission: { module: 'Support', resource: 'Ticket Settings', action: 'Edit', company: true } },
    ],
  },
  {
    iconKey: 'growth', label: 'Growth', path: '/growth',
    children: [
      { iconKey: 'wiki', label: 'Wiki', path: '/growth/wiki' },
      { iconKey: 'meetings', label: 'Meetings', path: '/growth/meetings' },
      { iconKey: 'marketing', label: 'Marketing', path: '/growth/marketing' },
      { iconKey: 'ideas', label: 'Ideas', path: '/growth/ideas' },
      { iconKey: 'documents', label: 'Documents', path: '/growth/documents' },
      { iconKey: 'reports', label: 'Reports', path: '/growth/reports' },
    ],
  },
  {
    iconKey: 'vault', label: 'Vault', path: '/vault',
    children: [
      { iconKey: 'dashboard', label: 'Dashboard', path: '/vault' },
      { iconKey: 'vaults', label: 'Vaults', path: '/vault/vaults', permission: { module: 'Vault', resource: 'Vault' } },
      { iconKey: 'approval', label: 'Access Requests', path: '/vault/requests', permission: { module: 'Vault', resource: 'Vault' } },
      { iconKey: 'rotation', label: 'Rotation', path: '/vault/rotation', permission: { module: 'Vault', resource: 'Vault' } },
      { iconKey: 'audit', label: 'Audit Log', path: '/vault/audit', permission: { module: 'Vault', resource: 'Vault' } },
    ],
  },
  {
    iconKey: 'admin', label: 'Admin', path: '/admin',
    children: [
      { iconKey: 'security', label: 'Platform Settings', path: '/admin/access' },
      { iconKey: 'organization', label: 'Organization', path: '/admin/organization' },
      { iconKey: 'orgchart', label: 'Org Chart', path: '/admin/orgchart' },
      { iconKey: 'users', label: 'Users', path: '/admin/users' },
      { iconKey: 'roles', label: 'Roles', path: '/admin/roles' },
      { iconKey: 'security', label: 'Security', path: '/admin/security' },
      { iconKey: 'audit', label: 'Audit', path: '/admin/audit' },
      { iconKey: 'subscription', label: 'Subscription', path: '/admin/subscription' },
      { iconKey: 'api', label: 'API', path: '/admin/api' },
      { iconKey: 'system', label: 'System', path: '/admin/system' },
    ],
  },
];

function initials(name?: string) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || 'U';
}

export function Sidebar({
  inSheet = false,
  onNavigate,
  collapsed = false,
  onToggle,
}: {
  inSheet?: boolean;
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  const { user, logout } = useAuthStore();
  const appName = useBrandingStore((s) => s.appName) || 'TeamOne';
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const ticketCount = useTicketStore((state) => state.tickets.length);

  const activeModule =
    navItems.find(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + '/') ||
        item.children?.some(
          (child) =>
            location.pathname === child.path ||
            location.pathname.startsWith(child.path + '/')
        )
    ) ||
    (location.pathname.startsWith('/settings')
      ? navItems.find((item) => item.label === 'Admin')
      : undefined) ||
    navItems[0];

  const hasPermission = useRbacStore((s) => s.hasPermission);
  const getScope = useRbacStore((s) => s.getScope);
  const permissionDataAvailable = useRbacStore((s) => s.isPermissionDataAvailable());
  const userRoles = user?.roles || [];
  const rbacRoles = useRbacStore((s) => s.roles);
  const effectiveRoles = getEffectiveRoles(userRoles, rbacRoles);
  // Super Admin (and the platform-level Admin role) bypass nav gating everywhere.
  // Support Admin / Support Manager / HR Admin etc. are NOT super admins — they
  // used to be folded in here, which let them see every hub's nav. Hub and
  // feature visibility for those roles now comes from their grants, below.
  const isSuperAdmin =
    user?.role === 'admin' ||
    user?.role === 'super_admin' ||
    effectiveRoles.some((r) => {
      const s = String(r || '').toLowerCase().replace(/[_\s-]+/g, '');
      return s === 'superadmin' || s === 'platformadmin' || s === 'admin';
    });

  const visibleChildren = (activeModule.children ?? []).filter((child) => {
    if (isSuperAdmin) {
      return true;
    }
    // Every hub (Support included) is now grant-driven: a child with no
    // permission is always shown; one with a permission is shown only when the
    // role holds that grant (Company scope when `company` is set).
    if (!child.permission) return true;
    if (!permissionDataAvailable) return true;
    const { module, resource, action = 'View', company } = child.permission;
    if (company) return getScope(module, resource, action) === 'Company';
    return hasPermission(module, resource, action);
  });

  const isActive = (path: string) => {
    if (path === activeModule.path) {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const go = (path: string) => {
    useTicketStore.getState().setCurrentTicket(null);
    navigate(path);
    onNavigate?.();
  };

  const ModuleIcon = IconMap[activeModule.iconKey];

  const compact = collapsed && !inSheet;

  return (
    <aside
      className={cn(
        'flex h-full shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[width] duration-150',
        compact ? 'w-[72px]' : 'w-[240px]',
        inSheet ? 'w-64' : 'border-r border-sidebar-border'
      )}
    >
      {/* ===== Brand (static — no workspace switcher) ===== */}
      <div className={cn('flex h-16 items-center', compact ? 'justify-center px-2' : 'px-3')}>
        <button type="button" onClick={() => go('/dashboard')} aria-label={`${appName} — go to dashboard`}
          className={cn('group flex w-full items-center rounded-lg py-1.5 transition-colors hover:bg-sidebar-accent/50',
            compact ? 'justify-center px-0' : 'gap-2.5 px-2')}>
          {/* The sidebar is a dark navy frame in both themes */}
          <Logo showText={false} isDark className="size-9" />
          {!compact && (
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate text-sm font-semibold leading-tight">{appName}</p>
            </div>
          )}
        </button>
      </div>

      {/* ===== Active module header + collapse toggle (same row) ===== */}
      <div className={cn('flex items-center gap-2 px-4 pb-2 pt-2', compact && 'justify-center px-0')}>
        {!compact && (
          <>
            {ModuleIcon && <ModuleIcon className="size-4 text-sidebar-active" />}
            <p className="flex-1 truncate font-display text-menu text-sidebar-foreground">
              {activeModule.label}
            </p>
          </>
        )}
        {onToggle && !inSheet && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex size-7 items-center justify-center rounded-md text-sidebar-muted transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
          >
            {compact ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <PanelLeftClose className="size-4" />
            )}
          </button>
        )}
      </div>

      {/* ===== Check in / out — surfaced in the mobile slide-out menu so it is
             reachable even when the top bar is too narrow to show it ===== */}
      {inSheet && (
        <div className="px-4 pb-2 pt-1 [&_button]:w-full [&_button]:justify-center">
          <CheckInOutButton className="w-full" />
        </div>
      )}

      {/* ===== Secondary nav: active module's sub-items ===== */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {visibleChildren.length > 0 ? (
          <nav className="space-y-0.5" aria-label={`${activeModule.label} navigation`}>
            {visibleChildren.map((child, idx) => {
              const ChildIcon = IconMap[child.iconKey];
              const active = isActive(child.path);
              const showSection = !compact && child.section && (idx === 0 || visibleChildren[idx - 1].section !== child.section);
              return (
                <div key={child.path} className="space-y-0.5">
                  {showSection && (
                    <div className={cn('px-2.5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted/70', idx === 0 && 'pt-1.5')}>
                      {child.section}
                    </div>
                  )}
                  <button
                    onClick={() => go(child.path)}
                    title={compact ? child.label : undefined}
                    className={cn(
                      'group flex w-full items-center rounded-lg text-xs font-medium transition-colors',
                      compact ? 'h-9 justify-center px-0' : 'h-8 justify-between px-2.5',
                      active
                        ? 'bg-sidebar-accent font-semibold text-sidebar-foreground'
                        : 'text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
                    )}
                  >
                    <div className={cn('flex items-center min-w-0', compact ? 'justify-center' : 'gap-2')}>
                      {ChildIcon && (
                        <ChildIcon
                          className={cn(
                            'size-4 shrink-0 transition-colors',
                            active
                              ? 'text-sidebar-foreground'
                              : 'text-sidebar-muted group-hover:text-sidebar-foreground'
                          )}
                        />
                      )}
                      {!compact && (
                        <span className="truncate">{child.label}</span>
                      )}
                    </div>
                    {!compact && child.badge !== undefined && (
                      <span className="ml-auto inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                        {child.badge}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </nav>
        ) : (
          <div className="px-2 py-3 text-xs text-sidebar-muted">
            {!compact && 'No sub-items'}
          </div>
        )}
      </div>

      {/* ===== User / Account Dropdown (pinned bottom) ===== */}
      <div className="border-t border-sidebar-border p-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'flex w-full items-center rounded-lg p-1.5 text-left transition-colors hover:bg-sidebar-accent',
                compact ? 'justify-center' : 'gap-2.5'
              )}
            >
              <Avatar className="size-8 shrink-0">
                <AvatarImage src={user?.avatar || getUserAvatarUrl(user?.name, user?.email)} alt={user?.name || 'User'} />
                <AvatarFallback className="text-xs font-medium bg-primary text-white">
                  {initials(user?.name)}
                </AvatarFallback>
              </Avatar>
              {!compact && (
                <>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-sidebar-foreground">
                      {user?.name || 'User'}
                    </p>
                    <p className="truncate text-[11px] text-sidebar-muted">
                      {user?.email || ''}
                    </p>
                  </div>
                  <ChevronsUpDown className="size-4 shrink-0 text-sidebar-muted" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align={compact ? 'center' : 'end'}
            side="top"
            sideOffset={8}
            className="w-56"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-xs font-medium leading-none">{user?.name || 'User'}</p>
                <p className="text-[11px] leading-none text-muted-foreground">
                  {user?.email || ''}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
              <UserIcon className="mr-2 size-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/admin/users?tab=access')}>
              <Settings className="mr-2 size-4" />
              <span>User &amp; Access Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 size-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

    </aside>
  );
}
